import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { api, formatApiError } from "@/lib/api";

/**
 * Dev/admin panel for testing /api/pricing/effective under different scopes.
 * UAT RC1d: SKU / Zone / Distributor are pickable by name+code (never raw UUIDs).
 */
export default function EffectivePriceTester() {
  const [skus, setSkus] = useState([]);
  const [zones, setZones] = useState([]);
  const [dists, setDists] = useState([]);

  const [skuId, setSkuId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [distributorId, setDistributorId] = useState("");
  const [qty, setQty] = useState(1);
  const [at, setAt] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, z, d] = await Promise.all([
          api.get("/skus", { params: { page: 1, page_size: 500, is_active: true } }),
          api.get("/zones", { params: { page: 1, page_size: 100 } }),
          api.get("/distributors", { params: { page: 1, page_size: 2000 } }),
        ]);
        if (!alive) return;
        setSkus((s.data.items || []).map(x => ({ id: x.id, code: x.sku_code, name: x.name || "" })));
        setZones((z.data.items || []).map(x => ({ id: x.id, code: x.code, name: x.name })));
        setDists((d.data.items || []).map(x => ({ id: x.id, code: x.code, name: x.name })));
      } catch (_e) { /* ignore, error surfaces via run() when user hits Compute */ }
    })();
    return () => { alive = false; };
  }, []);

  const run = async () => {
    setErr(null); setResult(null); setBusy(true);
    try {
      const params = { sku_id: skuId, qty };
      if (zoneId) params.zone_id = zoneId;
      if (distributorId) params.distributor_id = distributorId;
      if (at) params.at = at;
      const { data } = await api.get("/pricing/effective", { params });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="effective-price-tester">
      <CardContent className="p-5 space-y-3">
        <div className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
          <Search className="h-4 w-4 text-[#E5197F]" />
          Test effective price
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <Field label="SKU" className="lg:col-span-2">
            <SearchableSelect
              value={skuId} onChange={setSkuId} options={skus}
              placeholder="Search SKU…" testid="ept-sku"
            />
          </Field>
          <Field label="Zone">
            <SearchableSelect
              value={zoneId} onChange={setZoneId} options={zones}
              placeholder="Optional…" testid="ept-zone"
            />
          </Field>
          <Field label="Distributor">
            <SearchableSelect
              value={distributorId} onChange={setDistributorId} options={dists}
              placeholder="Optional…" testid="ept-dist"
            />
          </Field>
          <Field label="Qty"><Input type="number" value={qty} onChange={e=>setQty(+e.target.value || 1)} data-testid="ept-qty"/></Field>
          <div className="flex items-end">
            <Button onClick={run} disabled={busy || !skuId} className="w-full tulsi-gradient text-white" data-testid="ept-run">
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Compute"}
            </Button>
          </div>
          <Field label="At (optional ISO datetime)" className="md:col-span-2 lg:col-span-6">
            <Input value={at} onChange={e=>setAt(e.target.value)} placeholder="e.g. 2026-08-01T00:00:00Z" data-testid="ept-at"/>
          </Field>
        </div>
        {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="ept-err">{err}</div>}
        {result && (
          <div className="text-xs bg-neutral-50 border border-neutral-200 rounded p-3" data-testid="ept-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ResultCell label="Price" value={`₹ ${Number(result.price ?? 0).toLocaleString()}`} />
              <ResultCell label="Source" value={result.source} />
              <ResultCell label="Slab qty range"
                value={result.slab ? `${result.slab.min_qty}–${result.slab.max_qty ?? "∞"}` : "—"} />
              <ResultCell label="Valid from" value={result.valid_from || "—"} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ResultCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-sm font-semibold text-neutral-900">{value ?? "—"}</div>
    </div>
  );
}
